import { useEffect, useState } from 'react';
import type { CommsChannel, CommsKind, CommsTemplate } from '@salonomy/types';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '@salonomy/ui';
import { useCommsTemplates, useUpsertTemplate } from '../../data/comms.js';
import { ApiClientError } from '../../lib/api.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS8 — template management. Lists the salon's comms templates and offers an upsert-by-key editor
 * (PUT /comms/templates/:key, manager-only on the API). Templates are the reusable bodies the reminder
 * pipeline + campaigns draw from; editing one by its stable key is an upsert — a new key creates, an
 * existing key updates in place. Non-managers see the list read-only with an honest note.
 */

const CHANNELS: Array<{ value: CommsChannel; label: string }> = [
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push' },
];

const KINDS: Array<{ value: CommsKind; label: string }> = [
  { value: 'reminder', label: 'Reminder' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'gap_offer', label: 'Gap offer' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'manual', label: 'Manual' },
];

export function TemplatesPanel({ canManage }: { canManage: boolean }) {
  const { data: templates, isLoading, isError } = useCommsTemplates();
  const [editing, setEditing] = useState<CommsTemplate | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Templates
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Reusable message bodies for reminders and campaigns. Edit one by its key.
          </div>
        </div>
        {canManage ? (
          <Button variant="soft" size="sm" onClick={() => setCreating(true)}>
            New template
          </Button>
        ) : null}
      </div>

      <div style={{ marginTop: 14 }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
            <Spinner size={16} />
            <span className="muted" style={{ fontSize: 13 }}>
              Loading templates…
            </span>
          </div>
        ) : isError ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Couldn&rsquo;t load templates. Try again in a moment.
          </div>
        ) : !templates || templates.length === 0 ? (
          <EmptyState
            title="No templates yet"
            description={
              canManage
                ? 'Create a template to reuse a message across reminders and campaigns.'
                : 'A manager can add reusable message templates here.'
            }
          />
        ) : (
          <div className="comms-templates">
            {templates.map((t) => (
              <div className="comms-template-row" key={t.id}>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    {t.name}
                    {!t.active ? <Pill tone="neutral">Inactive</Pill> : null}
                  </div>
                  <div className="comms-template-row__key">
                    {t.key} · {t.channel} · {t.kind}
                  </div>
                </div>
                {canManage ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(t)}>
                    Edit
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {!canManage ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 12, lineHeight: 1.5 }}>
          Editing templates is a manager action. Ask an owner or manager to change a template.
        </div>
      ) : null}

      {(editing || creating) && canManage ? (
        <TemplateEditor
          existing={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      ) : null}
    </Card>
  );
}

/** Upsert-by-key editor. A new template lets you set the key; editing locks it (the key is the upsert id). */
function TemplateEditor({
  existing,
  onClose,
}: {
  existing: CommsTemplate | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const upsert = useUpsertTemplate();

  const [key, setKey] = useState(existing?.key ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [channel, setChannel] = useState<CommsChannel>(existing?.channel ?? 'sms');
  const [kind, setKind] = useState<CommsKind>(existing?.kind ?? 'reminder');
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [body, setBody] = useState(existing?.body ?? '');

  useEffect(() => {
    setKey(existing?.key ?? '');
    setName(existing?.name ?? '');
    setChannel(existing?.channel ?? 'sms');
    setKind(existing?.kind ?? 'reminder');
    setSubject(existing?.subject ?? '');
    setBody(existing?.body ?? '');
  }, [existing]);

  const isEmail = channel === 'email';
  const canSave =
    key.trim().length > 0 && name.trim().length > 0 && body.trim().length > 0 && !upsert.isPending;

  async function save() {
    try {
      await upsert.mutateAsync({
        key: key.trim(),
        body: {
          key: key.trim(),
          name: name.trim(),
          channel,
          kind,
          subject: isEmail && subject.trim() ? subject.trim() : null,
          body: body.trim(),
        },
      });
      toast.success(existing ? 'Template updated' : 'Template created');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save the template');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? `Edit “${existing.name}”` : 'New template'}
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onClose} disabled={upsert.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={!canSave}>
            {upsert.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create template'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field
          label="Key"
          hint={
            existing
              ? 'The key identifies the template and can’t change'
              : 'A stable slug, e.g. reminder-24h'
          }
        >
          <TextInput
            value={key}
            disabled={Boolean(existing)}
            placeholder="reminder-24h"
            onChange={(e) => setKey(e.target.value)}
          />
        </Field>
        <Field label="Name">
          <TextInput
            value={name}
            placeholder="24-hour reminder"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Channel">
            <Select value={channel} onChange={(e) => setChannel(e.target.value as CommsChannel)}>
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Kind">
            <Select value={kind} onChange={(e) => setKind(e.target.value as CommsKind)}>
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {isEmail ? (
          <Field label="Subject" hint="Email only">
            <TextInput
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Body" hint={`${body.length}/4000`}>
          <TextArea
            value={body}
            rows={6}
            maxLength={4000}
            placeholder="Hi {{firstName}}, this is a reminder about your appointment…"
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
